'use client'

import { ClientTextLink } from '@fukku/text-link/client/ClientTextLink'
import { LinkType } from '@fukku/text-link/utils'
import { SelectPromotion } from 'analytics/components/SelectPromotion/SelectPromotion'
import { cx } from 'classix'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'
import type { Cta } from '../../../../Home.type'
import styles from './CapsuleBannerCtas.module.css'

interface CapsuleBannerCtasProps {
	readonly inverse: boolean
	readonly ctas: Cta[]
}

const GAP_SMALL = 16
const GAP_MEDIUM = 24
const GAP_LARGE = 40

export function CapsuleBannerCtas({
	inverse,
	ctas,
}: CapsuleBannerCtasProps): ReactNode {
	const ctasRef = useRef<HTMLDivElement>(null)
	const { isMobile } = useMasterData()
	const [isColumn, setIsColumn] = useState<boolean>(isMobile)
	const [ctasWidth, setCtasWidth] = useState<number>(0)
	const [visibilityHidden, setVisibilityHidden] = useState<boolean>(true)
	const { isSmall, isMedium, isLargeOrGreater } = useResponsive()

	useEffect(() => {
		function handleCtasReposition() {
			const ctasContainer = ctasRef.current

			if (ctasContainer) {
				let ctasWidthComputed = ctasWidth
				if (!ctasWidth) {
					let ctasGap = 0
					if (isSmall) {
						ctasGap = GAP_SMALL
					}
					if (isMedium) {
						ctasGap = GAP_MEDIUM
					}
					if (isLargeOrGreater) {
						ctasGap = GAP_LARGE
					}
					const totalCtasWidth = Array.from(ctasContainer.children).reduce(
						(acc, child) => acc + (child as HTMLElement).offsetWidth,
						0
					)
					ctasWidthComputed = totalCtasWidth + (ctas?.length - 1) * ctasGap + 10
					setCtasWidth(ctasWidthComputed)
				}

				const isOverflowing = ctasWidthComputed > ctasContainer.clientWidth

				setIsColumn(isOverflowing)
				setVisibilityHidden(false)
			}
		}
		handleCtasReposition()

		window?.addEventListener('resize', handleCtasReposition)

		return () => {
			window?.removeEventListener('resize', handleCtasReposition)
		}
	}, [isSmall, isMedium, isLargeOrGreater])

	return (
		<nav
			ref={ctasRef}
			className={cx(
				styles.ctas,
				isColumn ? styles.column : '',
				visibilityHidden ? styles.hidden : ''
			)}
		>
			{ctas.map((cta) => (
				<SelectPromotion key={cta.ctaId} promotion={cta.promotion}>
					<ClientTextLink
						href={cta.href}
						className={styles.cta}
						linkProps={cta.linkProps}
						type={LinkType.Inspirational}
						inverse={inverse}
						isLocalizedPath={cta.isLocalizedPath}
						withLegacyLocale={cta.withLegacyLocale}
					>
						{cta.text}
					</ClientTextLink>
				</SelectPromotion>
			))}
		</nav>
	)
}
